import { useState, useRef, useEffect, useCallback } from "react";

interface UseDeleteConfirmationReturn {
  confirmingDeleteId: string | null;
  handleDeleteClick: (
    documentId: string,
    onConfirm: (id: string) => void
  ) => void;
}

/**
 * Manages two-tap delete confirmation with auto-reset timeout.
 * First tap sets the confirming state; second tap within the timeout
 * window triggers the actual delete callback.
 */
export const useDeleteConfirmation = (
  timeoutMs = 3000
): UseDeleteConfirmationReturn => {
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null
  );
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
    };
  }, []);

  const handleDeleteClick = useCallback(
    (documentId: string, onConfirm: (id: string) => void) => {
      if (confirmingDeleteId === documentId) {
        setConfirmingDeleteId(null);
        if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
        onConfirm(documentId);
      } else {
        setConfirmingDeleteId(documentId);
        if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = setTimeout(() => {
          setConfirmingDeleteId(null);
        }, timeoutMs);
      }
    },
    [confirmingDeleteId, timeoutMs]
  );

  return { confirmingDeleteId, handleDeleteClick };
};
